/* =========================================================================
 * au-geography.ts — the map's geometry layer.
 *
 * WHAT THIS IS
 *   The baked outlines of the eight Australian states/territories, the eight
 *   capital-city labels, the projection that turns [lon, lat] into SVG user
 *   units, the fixed viewBox, and the per-site label offsets. Pure data plus
 *   two pure functions: no React, no DOM, no colour.
 *
 * PORTED FROM
 *   /Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html
 *     line 2021  STATES  -> AU_STATES
 *     line 2022  CAPS    -> AU_CAPITALS
 *     line 2023  proj    -> project()
 *     line 2024  MAPVB   -> MAP_VIEWBOX
 *     line 2026  MLBL    -> MINE_LABEL_OFFSET
 *     line 2037  the `path()` helper inside mapSVG() -> polygonPoints()
 *
 * WHY THE POLYGONS ARE BAKED
 *   These rings are NOT derived from a geodata source (no Natural Earth, no
 *   ABS ASGS, no GeoJSON import). They are the demo's own hand-simplified
 *   outlines — a couple of dozen vertices per state, drawn to read well at
 *   ~470px wide and nothing more. They are copied here verbatim, digit for
 *   digit, because the whole point of the port is that the new map renders
 *   identically to the demo; re-deriving them from real geodata would change
 *   every pixel and would also drag a multi-megabyte dependency into a demo
 *   that needs none. Treat them as artwork, not as a cartographic dataset —
 *   they are not accurate enough to measure anything with.
 *
 * DECLARED OMISSIONS (relative to the demo)
 *   - Zoom (mapZoom / mapApply / the wheel + dblclick handlers, demo 2053+).
 *   - Pan (the pointer drag inside initMap).
 *   - Label dragging (MLPOS / mlPaint / mlReset, demo 2030-2036). Labels are
 *     rendered at their MINE_LABEL_OFFSET defaults and stay there, so the
 *     `gap`-trimmed leader-line maths in mlPaint has no port either — the
 *     leader line uses mapSVG()'s own default endpoints instead.
 *   Because zoom is gone, the counter-scaling of labels in mapApply() (the
 *   `1 / k ** 0.72` trick) is gone with it: at k = 1 it is the identity.
 * ========================================================================= */

/** One state or territory outline: display name plus a single closed ring. */
export interface StatePolygon {
  readonly n: string;
  readonly p: readonly (readonly [number, number])[];
}

/**
 * Demo line 2021. `STATES` — the eight hand-simplified state outlines,
 * keyed by state code, each `p` a ring of `[lon, lat]` pairs in degrees.
 * Coordinates copied verbatim from the demo.
 */
export const AU_STATES: Readonly<Record<string, StatePolygon>> = {
  QLD: {
    n: 'Queensland',
    p: [
      [142.5, -10.7], [145.3, -14.9], [146.2, -18.6], [149.2, -21.1],
      [150.8, -23.5], [153.1, -25.3], [153.5, -28.2], [152.0, -28.5],
      [150.0, -28.7], [148.9, -28.9], [141.0, -29.0], [141.0, -26.0],
      [138.0, -26.0], [138.0, -16.49], [139.2, -17.6], [140.9, -17.5],
      [141.6, -15.4], [141.6, -12.7],
    ],
  },
  NSW: {
    n: 'New South Wales',
    p: [
      [153.5, -28.2], [152.9, -31.4], [151.2, -33.9], [150.1, -36.4],
      [149.9, -37.5], [148.2, -36.1], [146.5, -35.9], [144.5, -35.8],
      [143.0, -34.7], [141.0, -34.0], [141.0, -29.0], [148.9, -28.9],
      [150.0, -28.7], [152.0, -28.5], [153.5, -28.2],
    ],
  },
  VIC: {
    n: 'Victoria',
    p: [
      [149.9, -37.5], [147.0, -38.6], [144.9, -38.3], [143.5, -38.8],
      [141.0, -38.1], [141.0, -34.0], [143.0, -34.7], [144.5, -35.8],
      [146.5, -35.9], [148.2, -36.1],
    ],
  },
  SA: {
    n: 'South Australia',
    p: [
      [141.0, -38.1], [139.7, -37.0], [138.5, -35.6], [137.6, -35.1],
      [136.8, -33.9], [135.6, -34.9], [134.2, -33.0], [131.0, -31.5],
      [129.0, -31.83], [129.0, -26.0], [141.0, -26.0],
    ],
  },
  WA: {
    n: 'Western Australia',
    p: [
      [129.0, -31.83], [128.0, -32.0], [125.0, -33.9], [123.5, -33.9],
      [121.9, -33.9], [119.0, -34.4], [117.9, -35.1], [115.1, -34.3],
      [115.7, -32.0], [114.6, -28.8], [113.4, -26.1], [113.7, -24.0],
      [114.1, -21.9], [116.7, -20.6], [118.6, -20.3], [121.0, -19.1],
      [122.2, -17.9], [123.5, -17.0], [125.2, -15.2], [126.8, -13.9],
      [128.3, -15.0], [129.0, -14.95],
    ],
  },
  NT: {
    n: 'Northern Territory',
    p: [
      [129.0, -14.95], [129.7, -14.9], [130.8, -12.4], [132.6, -11.4],
      [135.4, -12.2], [136.9, -12.2], [136.6, -14.2], [135.4, -15.3],
      [138.0, -16.49], [138.0, -26.0], [129.0, -26.0],
    ],
  },
  TAS: {
    n: 'Tasmania',
    p: [
      [144.7, -40.7], [148.3, -40.9], [148.3, -42.2], [147.0, -43.6],
      [145.3, -42.2],
    ],
  },
  ACT: {
    n: 'Australian Capital Territory',
    p: [
      [148.76, -35.12], [149.4, -35.29], [149.14, -35.92], [148.9, -35.6],
    ],
  },
};

/**
 * A capital-city label: the city name, its position in degrees, and the
 * text placement the demo hand-tuned so the eight labels never collide.
 */
export type CapitalLabel = readonly [
  name: string,
  lon: number,
  lat: number,
  anchor: 'start' | 'middle' | 'end',
  dx: number,
  dy: number,
];

/** Demo line 2022. `CAPS` — the eight capitals with their label offsets. */
export const AU_CAPITALS: readonly CapitalLabel[] = [
  ['Perth', 115.86, -31.95, 'start', 0, 12],
  ['Adelaide', 138.6, -34.93, 'end', -7, 4],
  ['Melbourne', 144.96, -37.81, 'middle', 0, 14],
  ['Sydney', 151.21, -33.87, 'end', -7, 3],
  ['Brisbane', 153.03, -27.47, 'end', -7, 3],
  ['Darwin', 130.84, -12.46, 'middle', 0, -8],
  ['Hobart', 147.33, -42.88, 'start', 6, 4],
  ['Canberra', 149.13, -35.28, 'end', -7, 11],
];

/**
 * Demo line 2024. `MAPVB` — `[minX, minY, width, height]`. Fixed: the map
 * never re-fits, so every render lands on the same coordinate space.
 */
export const MAP_VIEWBOX: readonly [number, number, number, number] = [-8, -8, 478, 436];

/**
 * Demo line 2023. `proj` — a plate-carrée (equirectangular) projection with
 * separate lon/lat scales, chosen by eye so the continent fills MAP_VIEWBOX.
 * No standard parallel, no ellipsoid: `(lon - 112) * 10.4` across and
 * `(-lat - 9.5) * 11.8` down, which is why the polygons above are only ever
 * valid against this one projection.
 */
export function project(lonLat: readonly [number, number]): [number, number] {
  const [lon, lat] = lonLat;
  return [(lon - 112) * 10.4, (-lat - 9.5) * 11.8];
}

/**
 * Demo line 2037 (the `path()` closure inside `mapSVG`). `project` applied to
 * a ring, formatted as an SVG `points` string with 1 decimal. The rounding is
 * load-bearing: it is what makes two renders of the same ring byte-identical.
 */
export function polygonPoints(ring: readonly (readonly [number, number])[]): string {
  return ring
    .map((p) => project(p).map((v) => v.toFixed(1)).join(','))
    .join(' ');
}

/**
 * Demo line 2026. `MLBL` — per-site label placement, hand-tuned so no two
 * site names collide: `[dx, nameDy, subDy, anchor]` in SVG user units,
 * relative to the pin. `subDy` is the second-line offset the demo reserved
 * for a sub-label; it is 0 for every site and is carried through unchanged.
 */
export const MINE_LABEL_OFFSET: Readonly<
  Record<string, readonly [dx: number, dy: number, subDy: number, anchor: string]>
> = {
  KG: [0, -17, 0, 'middle'],
  BR: [-14, 4, 0, 'end'],
  MI: [27, 4, 0, 'start'],
  OH: [-27, 4, 0, 'end'],
  GD: [-27, 4, 0, 'end'],
  MR: [0, -17, 0, 'middle'],
};
